import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Button,
  Grid,
  Card,
  CardActions,
  Stack,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import { InputField, InputSelect, GoogleMapAutocomplete } from '@sm-dashboard/form-provider';
import ClearIcon from '@mui/icons-material/Clear';
import { CopyAll, Delete, OpenInNew } from '@mui/icons-material';
import { UndoArrow } from '@/assets/icons';
import { canvaColor } from '@/constants';
import { Switch } from '@mui/material';
import {
  Edit,
  Delete as DeleteIcon,
  LocationOn,
  AddAPhoto as AddAPhotoIcon,
  Language as LanguageIcon,
  Facebook as FacebookIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAppContext } from '@/context/context';
import DynamicDialog from '@/components/dashboard/DaynmicDialog';

const cardBasicStyle = {
  borderRadius: 1,
  p: 4,
  mb: 2.5,
  border: '1px solid'
};

// אובייקט הסגנונות המשותפים
export const formStyles = {
  // סגנון הכרטיסים
  card: {
    ...cardBasicStyle,
    backgroundColor: 'common.white',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    borderColor: 'divider'
  },

  // סגנון הכרטיסים - מצב לא ציבורי (אפור)
  cardPrivate: {
    ...cardBasicStyle,
    backgroundColor: 'grey.300',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    borderColor: 'grey.300',
    opacity: 0.8
  },

  // סגנון הכותרות
  title: {
    mb: 3,
    fontWeight: 600,
    fontSize: '1.25rem',
    color: 'black',
    textAlign: 'left'
  },

  // סגנון הכותרות - מצב לא ציבורי (אפור)
  titlePrivate: {
    mb: 3,
    fontWeight: 600,
    fontSize: '1.25rem',
    color: 'grey.600',
    textAlign: 'left'
  },

  // סגנון הלייבלים
  fieldLabel: {
    display: 'block',
    fontWeight: 700,
    color: 'black',
    textAlign: 'start',
    mx: 2
  },

  // סגנון השדות
  inputField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 3,
      backgroundColor: '#f2f4f9',
      border: 'none',
      '& fieldset': {
        border: 'none'
      },
      '&:hover': {
        backgroundColor: 'grey.200'
      },
      '&.Mui-focused': {
        backgroundColor: 'common.white',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        border: 'none',
        '& fieldset': {
          border: 'none'
        }
      }
    }
  },

  // סגנון לכפתורי אייקונים
  iconButton: {
    width: 40,
    height: 40,
    backgroundColor: 'primary.main',
    color: 'common.white',
    '&:hover': {
      backgroundColor: 'primary.dark',
      transform: 'scale(1.05)',
      transition: 'all 0.2s ease-in-out'
    },
    '&:disabled': {
      backgroundColor: 'grey.300',
      color: 'grey.500'
    }
  },

  // סגנון לכפתורי אייקונים - מצב לא ציבורי (אפור)
  iconButtonPrivate: {
    width: 40,
    height: 40,
    backgroundColor: 'grey.400',
    color: 'grey.600',
    '&:hover': {
      backgroundColor: 'grey.500',
      transform: 'scale(1.05)',
      transition: 'all 0.2s ease-in-out'
    },
    '&:disabled': {
      backgroundColor: 'grey.300',
      color: 'grey.500'
    }
  },

  // סגנון לתמונות באפור (מצב לא ציבורי)
  imagePrivate: {
    filter: 'grayscale(1) brightness(0.8)',
    opacity: 0.7
  },

  // סגנון לכפתור ניקוי
  clearButton: {
    position: 'absolute',
    top: '50%',
    right: '10px',
    transform: 'translateY(-50%)',
    zIndex: 1,
    '&:hover': {
      transform: 'translateY(-50%)', // שמירה על מיקום אנכי קבוע
      transition: 'transform 0.2s ease-in-out',
      backgroundColor: 'primary.main' // שמירה על צבע הרקע
    }
  },

  // סגנון למיכל מרכזי
  centerContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 1
  },

  // סגנון לבוקס עם פוזיציה יחסית
  relativeBox: {
    position: 'relative'
  },

  // סגנון למלא רוחב
  fullWidth: {
    width: '100%'
  },

  // סגנון לכרטיס רקע (Grid containers)
  sectionCard: {
    bgcolor: 'background.default',
    p: 2,
    borderRadius: 2
  },

  // סגנון לכותרת עגולה מעוצבת
  roundedTitleBox: {
    borderRadius: '50px',
    px: 4,
    py: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },

  // סגנון לכפתור עגול
  roundedButton: {
    borderRadius: '50px',
    px: 3,
    py: 1.5,
    minWidth: '120px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    '&:hover': {
      transform: 'scale(1.05)',
      transition: 'transform 0.2s ease-in-out'
    }
  },

  // סגנון לכותרת עיקרית
  mainTitle: {
    color: 'text.primary',
    fontWeight: 600,
    textAlign: 'center'
  },

  // סגנון לכותרת קטגוריה
  categoryTitle: {
    p: 2,
    fontWeight: 600
  },

  // סגנון שורת כותרת עם קו שחור
  lineHeader: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    mt: 1,
    mb: 2,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: 0,
      right: 0,
      height: '2px',
      backgroundColor: 'common.black',
      opacity: 0.9,
      transform: 'translateY(-50%)'
    }
  },

  // תגית כותרת ימין
  lineHeaderTitle: {
    position: 'relative',
    backgroundColor: 'background.paper',
    px: 2,
    ml: 'auto',
    zIndex: 1,
    fontWeight: 700
  },

  // פריט תקנון קומפקטי
  regulationItem: {
    border: '1px solid',
    borderColor: 'grey.300',
    borderRadius: 2,
    bgcolor: 'grey.50',
    px: 2,
    py: 1.25,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
    }
  },

  // סגנון לעלאת קבצים
  fileUploadBox: {
    border: '2px dashed',
    borderColor: 'primary.main',
    borderRadius: 3,
    p: 4,
    textAlign: 'center',
    bgcolor: 'grey.50',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: 'primary.dark',
      bgcolor: 'primary.50',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)'
    }
  },

  // סגנון לעלאת קבצים עם קובץ נבחר
  fileUploadBoxWithFile: {
    borderColor: 'primary.main',
    bgcolor: 'primary.100',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
  },

  // מיכל אייקון העלאה
  uploadIconContainer: {
    width: 60,
    height: 60,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    transition: 'all 0.3s ease'
  },

  // מיכל התקדמות העלאה
  uploadProgressContainer: {
    width: '100%',
    mt: 2,
    p: 2,
    bgcolor: 'grey.50',
    borderRadius: 2
  },

  // פס התקדמות העלאה
  uploadProgressBar: {
    height: 8,
    borderRadius: 4,
    bgcolor: 'grey.200',
    '& .MuiLinearProgress-bar': {
      borderRadius: 4,
      bgcolor: 'primary.main'
    }
  },

  // סגנון לכפתור שליחה מעוצב
  submitButton: {
    minWidth: 120,
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
    '&:hover': {
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
      transform: 'translateY(-2px)'
    }
  },

  // סגנון לתוכן טקסט
  textContent: {
    whiteSpace: 'pre-wrap',
    lineHeight: 1.6,
    color: 'text.primary',
    fontSize: '1rem'
  },

  // קו הפרדה שחור לרוחב המסך
  dividerLine: {
    width: '96%',
    height: '2px',
    position: 'relative',
    left: '2%',
    backgroundColor: 'text.primary',
    mt: 10,
    mb: 4
  },

  // תמונת ת׳אמבנייל קטנה
  regulationThumb: {
    width: 36,
    height: 36,
    objectFit: 'cover',
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'grey.300',
    mr: 1
  },

  // סגנון לאנימציית ספינר
  spinAnimation: {
    '@keyframes iconSpin': {
      '0%': {
        transform: 'rotate(0deg)'
      },
      '100%': {
        transform: 'rotate(360deg)'
      }
    }
  },

  // סגנון לכפתור פעולה
  actionButton: {
    height: '45px',
    borderRadius: 3,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    fontSize: '0.875rem',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
      transition: 'all 0.2s ease-in-out'
    }
  },

  // סגנון לכפתור הוספה קטן
  addButton: {
    backgroundColor: 'success.main',
    color: 'white',
    '&:hover': {
      backgroundColor: 'success.dark'
    },
    width: 28,
    height: 28
  },

  // סגנון לקונטיינר flex עם גאפ
  flexContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 1
  },
  flexStart: {
    display: 'flex',
    justifyContent: 'flex-start'
  },

  colorfulButton: {
    borderRadius: 0.2,
    px: 2,
    py: 0.75,
    minWidth: '81px',
    height: '31px',
    fontWeight: 600,
    fontSize: '0.875rem',
    textTransform: 'none',
    boxShadow: 'none',
    transition: 'all 0.2s ease-in-out'
  },

  // צבעים לכפתורים
  colorfulButtonVariants: {
    copy: {
      backgroundColor: 'success.main',
      color: 'common.white',
      '&:hover': {
        backgroundColor: 'success.dark'
      }
    },
    view: {
      backgroundColor: 'warning.main',
      color: 'common.white',
      '&:hover': {
        backgroundColor: 'warning.dark'
      }
    }
  },

  // סגנון לכרטיס קמפיין
  campaignCard: {
    height: 260, // הגדלה מ-200 ל-260
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 1,
    backgroundColor: 'common.white', // הוספת רקע לבן מפורש
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    border: '1px solid',
    borderColor: 'divider',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)'
    }
  },

  // סגנון לחלק התמונה בכרטיס
  campaignImageBox: {
    height: 140, // הגדלה מ-120 ל-140
    overflow: 'hidden',
    backgroundColor: 'common.white' // שינוי מ-grey.50 ללבן
  },

  // סגנון לטייטל הכרטיס
  campaignTitle: {
    px: 2,
    py: 1,
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center'
  },

  // סגנון לטקסט הטייטל
  campaignTitleText: {
    color: 'black',
    fontSize: '16px',
    fontWeight: 400,
    textAlign: 'center',
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    mt: 2
  },

  // סגנון לכפתורי הפעולה בתחתית
  campaignActions: {
    p: 2,
    height: 'auto', // שינוי מ-60 ל-auto
    minHeight: 60
  },

  // סגנון לתמונה עם רווחים קטנים
  imageWithPadding: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: 0
  },

  // עיצובים לספר השלוחים
  StaffMembersBook: {
    // קונטיינר חיפוש - יותר קרוב לראש הכרטיס
    searchContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: { md: 'flex-start', xs: 'flex-start' },
      height: { md: '100%', xs: '45%' },
      width: { md: '85%', xs: '95%' },
      gap: { xs: 1, md: 1.5 },
      pt: { md: 4, xs: 4 } // הגדלה מ-4 ל-6 ומ-3 ל-4 כדי להזיז למעלה
    },

    // קונטיינר תוצאות - תיקון הגלילה
    resultsContainer: {
      display: 'flex',
      flexDirection: 'column',
      height: { md: '100%', xs: '95%' },
      width: { md: '85%', xs: '95%' },
      position: 'relative'
    },

    // Stack לתוצאות - כאן הגלילה צריכה להיות
    resultsStack: {
      width: '100%',
      flexGrow: 1,
      maxHeight: 'calc(100vh - 200px)', // גובה מקסימלי
      overflowY: 'auto', // גלילה אנכית
      paddingRight: 1,
      pt: 1,
      '&::-webkit-scrollbar': {
        width: 6
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: 'rgba(0,0,0,0.1)',
        borderRadius: 3
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'primary.main',
        borderRadius: 3
      }
    },

    // פרטי שליח קומפקטיים - ללא גלילה פנימית
    compactDetails: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: 0.8,
      pt: 0
    },

    // כפתור אייקון צבעוני עם רקע לבן
    coloredIconButton: {
      border: '1px solid',
      borderRadius: '50%',
      width: 32,
      height: 32,
      backgroundColor: 'white', // רקע לבן לאייקון
      '& svg': {
        fill: 'currentColor'
      }
    },

    // Box לאייקון
    iconBox: {
      width: 16,
      height: 16,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'inherit',
      '& svg': {
        fill: ''
      }
    },

    // כפתור חזרה - עם אייקון קטן יותר
    backButton: {
      backgroundColor: 'primary.dark',
      height: '30px',
      color: 'white',
      borderRadius: '25px',
      px: 3,
      py: 1,
      textTransform: 'none',
      fontSize: '14px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      '& .MuiButton-startIcon': {
        marginRight: 1,
        '& svg': {
          width: '30px', // הקטנה מ-36px ל-20px
          height: '30px'
        }
      },
      '&:hover': {
        backgroundColor: 'primary.dark',
        transform: 'scale(1.05)',
        transition: 'all 0.2s ease-in-out'
      }
    },

    // כרטיס מפה עם גבול
    mapCard: {
      flexGrow: 1,
      borderRadius: 3,
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      border: '2px solid',
      borderColor: 'divider'
    }
  },

  // עיצובים חדשים לאירועים
  events: {
    // כרטיס ראשי עם רקע כתום
    mainCard: {
      backgroundColor: 'warning.main',
      borderRadius: 4,
      p: 3,
      mb: 3,
      boxShadow: '0 8px 24px rgba(243, 134, 39, 0.3)',
      position: 'relative'
    },

    // כותרת עם רקע אפור אליפטי (כמו במוקדים שלי)
    sectionTitle: {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      borderRadius: '50px',
      px: 4,
      py: 1.5,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      justifyContent: 'center',
      mb: 3
    },

    // אזור הגלילה האופקית
    scrollContainer: {
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '20px',
        background: 'linear-gradient(to right, rgba(243, 134, 39, 1), transparent)',
        zIndex: 2,
        pointerEvents: 'none'
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        width: '20px',
        background: 'linear-gradient(to left, rgba(243, 134, 39, 1), transparent)',
        zIndex: 2,
        pointerEvents: 'none'
      }
    },

    // אזור הגלילה הפנימי
    scrollBox: {
      display: 'flex',
      justifyContent: 'flex-start', // ברירת מחדל
      gap: 2,
      overflowX: 'auto',
      pb: 1,
      px: 1,
      '::-webkit-scrollbar': {
        height: '6px'
      },
      '::-webkit-scrollbar-track': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        borderRadius: '3px'
      },
      '::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        borderRadius: '3px',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.7)'
        }
      }
    },

    // כרטיסיית אירוע
    eventCard: {
      width: '140px', // רוחב קבוע במקום minWidth
      height: '160px',
      cursor: 'pointer',
      backgroundColor: 'white',
      borderRadius: 1,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.3s ease-in-out',
      position: 'relative',
      flexShrink: 0, // מונע התכווצות
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)'
      }
    },

    // כרטיסיה נבחרת
    selectedEventCard: {
      border: '3px solid',
      borderColor: 'primary.main',
      boxShadow: '0 8px 20px rgba(152, 18, 53, 0.3)'
    },

    // תוכן הכרטיסיה
    eventCardContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      textAlign: 'center',
      p: 2,
      position: 'relative'
    },

    // מעגל האייקון
    iconCircle: {
      width: '50px',
      height: '50px',
      borderRadius: '50%',
      backgroundColor: 'grey.100',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      mb: 1,
      border: '2px solid transparent'
    },

    // מעגל אייקון נבחר
    selectedIconCircle: {
      borderColor: 'primary.main'
    },

    // טקסט כרטיסיה
    eventCardText: {
      fontSize: '11px',
      fontWeight: 600,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      lineHeight: 1.2,
      color: 'text.primary',
      width: '100%', // רוחב מלא של הקונטיינר
      textAlign: 'center',
      wordBreak: 'break-word'
    },

    // טקסט כרטיסיה נבחרת
    selectedEventCardText: {
      color: 'primary.main'
    },

    // כפתור הוספה עגול
    addButton: {
      px: 4,
      py: 1.5,
      borderRadius: '50px',
      boxShadow: '0 4px 12px rgba(152, 18, 53, 0.3)',
      color: 'white',
      fontWeight: 600,
      '&:hover': {
        transform: 'scale(1.05)',
        transition: 'transform 0.2s ease-in-out',
        boxShadow: '0 6px 16px rgba(152, 18, 53, 0.4)'
      },
      '&:disabled': {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        color: 'rgba(255, 255, 255, 0.7)'
      }
    },

    // כפתור חיץ גלילה
    scrollArrow: {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      color: 'primary.main',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
      border: '1px solid rgba(0, 0, 0, 0.1)',
      zIndex: 3,
      '&:hover': {
        backgroundColor: 'white',
        transform: 'translateY(-50%) scale(1.1)',
        transition: 'all 0.2s ease-in-out'
      }
    },

    // חיץ שמאל
    leftArrow: {
      left: '8px'
    },

    // חיץ ימין
    rightArrow: {
      right: '8px'
    },

    // כפתור פלוס על כרטיסיה
    cardPlusButton: {
      position: 'absolute',
      top: '8px',
      left: '8px',
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: 'success.main',
      color: 'white',
      minWidth: 'unset',
      padding: 0,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
      '&:hover': {
        backgroundColor: 'success.dark',
        transform: 'scale(1.1)',
        transition: 'all 0.2s ease-in-out'
      }
    },

    // כרטיס אירוע בתצוגה
    displayEventCard: {
      height: 200,
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 3,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      border: '1px solid',
      borderColor: 'divider',
      transition: 'all 0.3s ease-in-out',
      position: 'relative',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)'
      }
    },

    // אירוע פעיל
    activeEventCard: {
      borderColor: 'success.main',
      backgroundColor: 'rgba(148, 193, 31, 0.02)'
    },

    // אירוע לא פעיל
    inactiveEventCard: {
      borderColor: 'grey.300',
      backgroundColor: 'rgba(0, 0, 0, 0.02)',
      opacity: 0.7
    },

    // תוכן כרטיס אירוע
    displayEventContent: {
      p: 2,
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    },

    // כותרת אירוע
    eventTitle: {
      fontSize: '14px',
      fontWeight: 600,
      textAlign: 'center',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      mb: 1
    },

    // פעולות אירוע
    eventActions: {
      p: 1.5,
      borderTop: '1px solid',
      borderColor: 'divider'
    },

    // תווית סטטוס
    statusBadge: {
      position: 'absolute',
      top: '8px',
      right: '8px',
      px: 1.5,
      py: 0.5,
      borderRadius: '12px',
      fontSize: '10px',
      fontWeight: 600,
      color: 'white'
    },

    // תווית פעיל
    activeBadge: {
      backgroundColor: 'success.main'
    },

    // תווית לא פעיל
    inactiveBadge: {
      backgroundColor: 'grey.500'
    },

    // תצוגת אירועים - אזור עם גלילה מוגבל
    eventsDisplayBox: {
      maxHeight: '360px', // גובה של 3 שורות (120px * 3)
      overflow: 'auto',
      '::-webkit-scrollbar': {
        width: '6px'
      },
      '::-webkit-scrollbar-track': {
        backgroundColor: 'rgba(0,0,0,0.1)',
        borderRadius: '3px'
      },
      '::-webkit-scrollbar-thumb': {
        backgroundColor: 'success.main',
        borderRadius: '3px'
      }
    },

    // תצוגת אירועים לא פעילים - אזור עם גלילה מוגבל
    inactiveEventsDisplayBox: {
      maxHeight: '240px', // גובה של 2 שורות (120px * 2)
      overflow: 'auto',
      '::-webkit-scrollbar': {
        width: '6px'
      },
      '::-webkit-scrollbar-track': {
        backgroundColor: 'rgba(0,0,0,0.1)',
        borderRadius: '3px'
      },
      '::-webkit-scrollbar-thumb': {
        backgroundColor: 'grey.500',
        borderRadius: '3px'
      }
    },

    // תווית סטטוס פעיל
    activeStatusBadge: {
      position: 'absolute',
      top: 8,
      right: 8,
      backgroundColor: 'success.main',
      color: 'white',
      px: 1,
      py: 0.5,
      borderRadius: '12px',
      fontSize: '10px',
      fontWeight: 600,
      zIndex: 2
    },

    // תווית סטטוס לא פעיל
    inactiveStatusBadge: {
      position: 'absolute',
      top: 8,
      right: 8,
      backgroundColor: 'grey.500',
      color: 'white',
      px: 1,
      py: 0.5,
      borderRadius: '12px',
      fontSize: '10px',
      fontWeight: 600,
      zIndex: 2
    },

    // כפתור מחיקה מעל כרטיס
    deleteButtonOverlay: {
      position: 'absolute',
      top: 8,
      left: 8,
      width: 24,
      height: 24,
      opacity: 0,
      transition: 'opacity 0.2s ease-in-out',
      zIndex: 2,
      '&:hover': {
        opacity: 1
      }
    },

    // כרטיס אירוע עם עמעום
    eventCardContainer: {
      position: 'relative',
      height: '200px'
    },

    // כרטיס אירוע לא פעיל עם עמעום
    inactiveEventCardContainer: {
      position: 'relative',
      height: '200px',
      opacity: 0.7
    },

    // הודעת "אין נתונים"
    noDataContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '200px',
      color: 'text.secondary'
    },

    // כפתור הוספה מעל כרטיס
    addButtonOverlay: {
      position: 'absolute',
      top: 8,
      left: 40,
      width: 24,
      height: 24,
      backgroundColor: 'success.main',
      '&:hover': {
        backgroundColor: 'success.dark',
        transform: 'scale(1.1)'
      }
    },

    // עיצוב לכרטיסים לא פעילים
    inactiveCardWrapper: {
      opacity: 0.7,
      filter: 'grayscale(30%)'
    },

    // עיצוב לסוויץ באירועים
    eventStatusSwitch: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      mt: 1,
      mb: 1,
      '& .MuiSwitch-root': {
        width: 42,
        height: 26,
        padding: 0,
        '& .MuiSwitch-switchBase': {
          padding: 0,
          margin: 2,
          transitionDuration: '300ms',
          '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
              backgroundColor: 'success.main',
              opacity: 1,
              border: 0
            }
          }
        },
        '& .MuiSwitch-thumb': {
          boxSizing: 'border-box',
          width: 22,
          height: 22
        },
        '& .MuiSwitch-track': {
          borderRadius: 13,
          backgroundColor: 'grey.400',
          opacity: 1,
          transition: 'background-color 500ms'
        }
      }
    },

    // טקסט הסוויץ
    switchLabel: {
      fontSize: '12px',
      fontWeight: 500,
      color: 'text.secondary',
      textAlign: 'center'
    },

    // עיצוב לאייקון אירוע לא פעיל
    inactiveIcon: {
      filter: 'grayscale(100%)',
      opacity: 0.6
    }
  },

  // עיצובים לטפסי מידע אישי ובתי חב"ד
  forms: {
    // שדה עם אייקון משמאל
    fieldWithIcon: {
      display: 'flex',
      alignItems: 'center',
      gap: 1
    },

    // איזור flex שתופס את כל הרוחב
    flexBox: {
      flex: 1
    },

    // כפתור אתר אינטרנט מעוצב
    websiteButton: {
      width: 40,
      height: 40,
      minWidth: 40,
      '&:disabled': {
        pointerEvents: 'none',
        backgroundColor: 'warning.main',
        color: 'common.white'
      }
    },

    // כפתור פייסבוק מעוצב
    facebookButton: {
      width: 40,
      height: 40,
      minWidth: 40,
      borderRadius: '50%',
      backgroundColor: '#1877F2',
      '&:disabled': {
        backgroundColor: '#1877F2'
      },
      '&:hover': {
        backgroundColor: '#166fe5'
      }
    },

    // אייקון פייסבוק לבן
    facebookIcon: {
      color: 'white',
      fontSize: 20
    },

    // תצוגה מקדימה של תמונה
    imagePreviewButton: {
      p: 0,
      borderRadius: 2,
      overflow: 'hidden',
      width: '120px',
      height: '120px'
    },

    // סגנון התמונה בתצוגה מקדימה
    previewImageStyle: {
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const,
      borderRadius: '8px'
    },

    // טקסט תיאור תמונה
    imageCaption: {
      color: 'text.secondary',
      fontSize: '0.75rem',
      fontWeight: 400,
      textAlign: 'center',
      mt: 0.5,
      mb: 1
    },

    // כפתור מחיקה קטן
    smallDeleteButton: {
      fontSize: '0.7rem',
      height: '28px',
      borderRadius: 2
    },

    // כפתור העלאה מקווקו
    dashedUploadButton: {
      border: '2px dashed',
      borderColor: 'warning.main',
      width: 60,
      height: 60
    },

    // קונטיינר מדיה במצב קריאה בלבד
    readOnlyMediaContainer: {
      pt: 1,
      mt: 2,
      p: 2,
      bgcolor: 'grey.50',
      borderRadius: 2,
      display: 'flex',
      gap: 2,
      flexDirection: 'column'
    },

    // סגנון מדיה במצב קריאה בלבד
    readOnlyMediaStyle: {
      width: '100%',
      maxWidth: '200px',
      borderRadius: '4px'
    }
  }
};

// קומפוננט משותף לכרטיס טופס
interface FormCardProps {
  title: string;
  children: React.ReactNode;
  sx?: any;
  isPublic?: boolean; // הוספת prop לבדיקה אם הכרטיס ציבורי
}

export const FormCard: React.FC<FormCardProps> = ({ title, children, sx, isPublic = true }) => {
  // בחירת הסגנון לפי מצב הציבוריות
  const cardStyle = isPublic ? formStyles.card : formStyles.cardPrivate;
  const titleStyle = isPublic ? formStyles.title : formStyles.titlePrivate;

  return (
    <Box sx={sx ? { ...cardStyle, ...sx } : cardStyle}>
      <Typography variant="h6" sx={titleStyle}>
        {title}
      </Typography>
      {children}
    </Box>
  );
};

// קומפוננט משותף ללייבל שדה
interface FieldLabelProps {
  label: string;
  sx?: any;
}

export const FieldLabel: React.FC<FieldLabelProps> = ({ label, sx }) => {
  return (
    <Typography variant="caption" sx={sx ? { ...formStyles.fieldLabel, ...sx } : formStyles.fieldLabel}>
      {label}
    </Typography>
  );
};

// קומפוננט משותף לשדה קלט
interface StyledInputFieldProps {
  name: string;
  label?: string;
  readOnly?: boolean;
  helperText?: string;
  sx?: any;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  multiline?: boolean;
  rows?: number;
  required?: boolean;
}

export const StyledInputField: React.FC<StyledInputFieldProps> = ({
  name,
  label = '',
  readOnly = false,
  helperText,
  sx,
  multiline
}) => {
  return (
    <InputField name={name} label={label} readOnly={readOnly} helperText={helperText} sx={sx} multiline={multiline} />
  );
};

// קומפוננט משותף לסלקט
interface StyledFormSelectProps {
  name: string;
  options: { value: string; label: string }[];
  readOnly?: boolean;
  sx?: any;
  required?: boolean;
  onChange?: (event: any) => void;
}

export const StyledFormSelect: React.FC<StyledFormSelectProps> = ({
  name,
  options,
  readOnly = false,
  sx,
  required,
  onChange
}) => {
  return (
    <Box sx={{ ...formStyles.inputField, ...sx }}>
      <InputSelect name={name} options={options} readOnly={readOnly} onChange={onChange} />
    </Box>
  );
};

// קומפוננט משותף לשדה עם לייבל
interface FormFieldProps {
  label: string;
  name: string;
  readOnly?: boolean;
  helperText?: string;
  type?: 'input' | 'select' | 'number';
  options?: { value: string; label: string }[];
  sx?: any;
  required?: boolean;
  onChange?: (event: any) => void;
  inputType?: string;
  multiline?: boolean;
  rows?: number;
}

export const FormField: React.FC<FormFieldProps> = ({
  label,
  name,
  type = 'input',
  options,
  readOnly = false,
  helperText,
  sx,
  required,
  onChange,
  inputType,
  multiline,
  rows
}) => {
  return type === 'select' ? (
    <>
      {label && <FieldLabel label={label} />}
      <StyledFormSelect
        name={name}
        options={options || []}
        readOnly={readOnly}
        onChange={onChange}
        required={required}
      />
    </>
  ) : (
    <StyledInputField
      name={name}
      label={label}
      type={type}
      readOnly={readOnly}
      helperText={helperText}
      sx={sx}
      multiline={multiline}
      required={required}
    />
  );
};

// קומפוננט משותף לכפתור אייקון
interface StyledIconButtonProps {
  onClick: () => void;
  disabled?: boolean;
  icon: React.ReactNode;
  tooltip?: string;
  variant?: 'primary' | 'success' | 'warning' | 'error' | 'grey' | 'info';
  sx?: any;
  isPublic?: boolean; // הוספת prop למצב ציבורי
}

export const StyledIconButton: React.FC<StyledIconButtonProps> = ({
  onClick,
  disabled = false,
  icon,
  tooltip,
  variant = 'primary',
  sx,
  isPublic = true
}) => {
  const variantColors = {
    primary: { main: 'primary.main', dark: 'primary.dark' },
    success: { main: 'success.main', dark: 'success.dark' },
    warning: { main: 'warning.main', dark: 'warning.dark' },
    info: { main: 'info.main', dark: 'info.dark' },
    grey: { main: 'grey.400', dark: 'grey.500' },
    error: { main: 'error.main', dark: 'error.dark' }
  };

  // בחירת הסגנון לפי מצב הציבוריות
  const baseIconStyle = isPublic ? formStyles.iconButton : formStyles.iconButtonPrivate;

  const buttonSx = isPublic
    ? {
        ...baseIconStyle,
        backgroundColor: variantColors[variant].main,
        '&:hover': {
          ...baseIconStyle['&:hover'],
          backgroundColor: variantColors[variant].dark
        },
        ...sx
      }
    : {
        ...baseIconStyle,
        ...sx
      };

  const button = (
    <IconButton onClick={onClick} disabled={disabled} sx={buttonSx}>
      {icon}
    </IconButton>
  );

  return tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button;
};

// קומפוננט משותף לשדה עם כפתור ניקוי
interface FieldWithClearProps {
  label: string;
  value: string;
  onClear: () => void;
  children: React.ReactNode;
  showClear?: boolean;
}

export const FieldWithClear: React.FC<FieldWithClearProps> = ({
  label,
  value,
  onClear,
  children,
  showClear = true
}) => {
  return (
    <>
      <FieldLabel label={label} />
      <Box sx={formStyles.relativeBox}>
        {children}
        {showClear && value && (
          <StyledIconButton
            onClick={onClear}
            icon={<ClearIcon fontSize="small" />}
            tooltip="נקה שדה"
            variant="primary"
            sx={formStyles.clearButton}
          />
        )}
      </Box>
    </>
  );
};

// קומפוננט משותף לכרטיס סקציה
interface SectionCardProps {
  children: React.ReactNode;
  sx?: any;
}

export const SectionCard: React.FC<SectionCardProps> = ({ children, sx }) => {
  return (
    <Grid container sx={{ ...formStyles.sectionCard, ...sx }}>
      {children}
    </Grid>
  );
};

// קומפוננט משותף לכותרת מעוצבת עגולה
interface RoundedTitleProps {
  title: string;
  variant?: 'h4' | 'h5' | 'h6';
  sx?: any;
}

export const RoundedTitle: React.FC<RoundedTitleProps> = ({ title, variant = 'h5', sx }) => {
  return (
    <Box sx={{ ...formStyles.roundedTitleBox, ...sx }}>
      <Typography variant={variant} sx={formStyles.mainTitle}>
        {title}
      </Typography>
    </Box>
  );
};

// קומפוננט משותף לכפתור פעולה עגול
interface RoundedActionButtonProps {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'contained' | 'outlined';
  color?: 'primary' | 'secondary' | 'error';
  loading?: boolean;
  icon?: React.ReactNode;
  sx?: any;
}

export const RoundedActionButton: React.FC<RoundedActionButtonProps> = ({
  children,
  onClick,
  disabled = false,
  variant = 'contained',
  color = 'primary',
  loading = false,
  icon,
  sx
}) => {
  return (
    <Button
      variant={variant}
      color={color}
      onClick={onClick}
      disabled={disabled || loading}
      startIcon={
        icon ? (
          <Box
            sx={{
              animation: loading ? 'iconSpin 1s linear infinite' : 'none',
              ...formStyles.spinAnimation
            }}
          >
            {icon}
          </Box>
        ) : undefined
      }
      sx={{ ...formStyles.roundedButton, ...sx }}
    >
      {children}
    </Button>
  );
};

// קומפוננט משותף לכותרת קטגוריה
interface CategoryTitleProps {
  title: string;
  variant?: 'h6' | 'subtitle1';
  sx?: any;
}

export const CategoryTitle: React.FC<CategoryTitleProps> = ({ title, variant = 'h6', sx }) => {
  return (
    <Typography variant={variant} sx={{ ...formStyles.categoryTitle, ...sx }}>
      {title}
    </Typography>
  );
};

// קומפוננט לכפתורי פעולה צבעוניים
interface ColorfulActionButtonProps {
  children: React.ReactNode;
  onClick: () => void;
  variant: 'copy' | 'view';
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  sx?: any;
}

export const ColorfulActionButton: React.FC<ColorfulActionButtonProps> = ({
  children,
  onClick,
  variant,
  disabled = false,
  loading = false,
  icon,
  size = 'medium',
  sx
}) => {
  const sizeStyles = {
    small: { px: 1.5, py: 0.5, fontSize: '0.7rem', minWidth: '70px', height: '32px' },
    medium: { px: 2, py: 0.75, fontSize: '0.75rem', minWidth: '81px', height: '34px' },
    large: { px: 2.5, py: 1, fontSize: '0.85rem', minWidth: '90px', height: '36px' }
  };

  return (
    <Button
      onClick={onClick}
      disabled={disabled || loading}
      startIcon={
        icon ? (
          <Box
            sx={{
              animation: loading ? 'iconSpin 1s linear infinite' : 'none',
              ...formStyles.spinAnimation
            }}
          >
            {icon}
          </Box>
        ) : undefined
      }
      sx={{
        ...formStyles.colorfulButton,
        ...formStyles.colorfulButtonVariants[variant],
        ...sizeStyles[size],
        ...sx
      }}
    >
      {children}
    </Button>
  );
};

// קומפוננט משותף לכרטיס קמפיין
interface CampaignCardProps {
  title: string;
  link: string;
  onView: () => void;
  onCopy: () => void;
  imageComponent: React.ReactNode;
}

export const CampaignCard: React.FC<CampaignCardProps> = ({ title, link, onView, onCopy, imageComponent }) => {
  return (
    <Card sx={formStyles.campaignCard}>
      {/* חלק התמונה */}
      <Box sx={formStyles.campaignImageBox}>{imageComponent}</Box>

      {/* הטייטל */}
      <Box sx={formStyles.campaignTitle}>
        <Typography sx={formStyles.campaignTitleText}>{title}</Typography>
      </Box>

      {/* הכפתורים */}
      <CardActions sx={{ ...formStyles.flexContainer, ...formStyles.campaignActions }}>
        <ColorfulActionButton
          onClick={onView}
          variant="view"
          icon={<OpenInNew fontSize="small" />}
          size="small"
          sx={{ flex: 1 }}
        >
          צפייה
        </ColorfulActionButton>

        <ColorfulActionButton
          onClick={onCopy}
          variant="copy"
          icon={<CopyAll fontSize="small" />}
          size="small"
          sx={{ flex: 1 }}
        >
          העתק
        </ColorfulActionButton>
      </CardActions>
    </Card>
  );
};

// קומפוננט משותף לכפתור פייסבוק
interface FacebookButtonProps {
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  icon: React.ReactNode;
  isPublic?: boolean;
}

export const FacebookButton: React.FC<FacebookButtonProps> = ({
  onClick,
  disabled = false,
  title,
  icon,
  isPublic = true
}) => {
  // בחירת הסגנון לפי מצב הציבוריות
  const buttonStyle = isPublic
    ? formStyles.forms.facebookButton
    : {
        ...formStyles.forms.facebookButton,
        backgroundColor: 'grey.400',
        '&:hover': {
          backgroundColor: 'grey.500'
        }
      };

  return (
    <IconButton onClick={onClick} disabled={disabled} sx={buttonStyle} title={title}>
      <Box sx={formStyles.forms.facebookIcon}>{icon}</Box>
    </IconButton>
  );
};

// קומפוננט משותף לתצוגת מדיה במצב קריאה בלבד
interface ReadOnlyMediaDisplayProps {
  videoUrl?: string;
  imageUrl?: string;
  imageAlt?: string;
  isPublic?: boolean;
}

export const ReadOnlyMediaDisplay: React.FC<ReadOnlyMediaDisplayProps> = ({
  videoUrl,
  imageUrl,
  imageAlt,
  isPublic = true
}) => {
  if (!videoUrl && !imageUrl) return null;

  return (
    <Box sx={{ pt: 1 }}>
      <Box sx={formStyles.forms.readOnlyMediaContainer}>
        {videoUrl && (
          <Box>
            <video
              src={videoUrl}
              controls
              style={{
                ...formStyles.forms.readOnlyMediaStyle,
                ...(isPublic ? {} : formStyles.imagePrivate)
              }}
            />
          </Box>
        )}
        {imageUrl && (
          <Box>
            <img
              src={imageUrl}
              alt={imageAlt || 'תמונה'}
              style={{
                ...formStyles.forms.readOnlyMediaStyle,
                ...(isPublic ? {} : formStyles.imagePrivate)
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// קומפוננט משותף לכרטיס אירוע עם כפתורי עריכה ומחיקה
interface EventDisplayCardProps {
  title: string;
  address?: string; // הוספת שדה כתובת
  onEdit: () => void;
  onDelete: () => void;
  onToggleStatus: (checked: boolean) => void;
  isActive: boolean;
  imageComponent: React.ReactNode;
}

export const EventDisplayCard: React.FC<EventDisplayCardProps> = ({
  title,
  address, // הוספת הכתובת
  onEdit,
  onDelete,
  onToggleStatus,
  isActive,
  imageComponent
}) => {
  return (
    <Card sx={formStyles.campaignCard}>
      {/* חלק התמונה */}
      <Box sx={formStyles.campaignImageBox}>{imageComponent}</Box>

      {/* הטייטל בלבד */}
      <Box sx={formStyles.campaignTitle}>
        <Typography sx={formStyles.campaignTitleText}>{title}</Typography>
      </Box>

      {/* הכתובת עם אייקון מיקום - בשורה נפרדת */}
      {address && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            px: 2,
            pb: 1
          }}
        >
          <LocationOn sx={{ fontSize: '16px', color: 'grey.600' }} />
          <Typography
            sx={{
              fontSize: '11px',
              color: 'grey.700',
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '140px',
              fontWeight: 500
            }}
          >
            {address}
          </Typography>
        </Box>
      )}

      {/* הכפתורים */}
      <CardActions
        sx={{
          ...formStyles.flexContainer,
          ...formStyles.campaignActions,
          flexDirection: 'column',
          p: 1.5,
          gap: 1
        }}
      >
        {/* שורת הכפתורים */}
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <ColorfulActionButton
            onClick={onEdit}
            variant="view"
            icon={<Edit fontSize="small" />}
            size="small"
            sx={{ flex: 1 }}
          >
            עריכה
          </ColorfulActionButton>

          <ColorfulActionButton
            onClick={onDelete}
            variant="copy"
            size="small"
            sx={{
              flex: 0,
              minWidth: '40px',
              width: '40px',
              backgroundColor: 'error.main',
              '&:hover': { backgroundColor: 'error.dark' },
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Delete fontSize="small" />
          </ColorfulActionButton>
        </Box>

        {/* סוויץ */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pt: 0.5,
            mb: 2
          }}
        >
          <Tooltip title={isActive ? 'לחץ לכיבוי המוקד' : 'לחץ להפעלת המוקד'} placement="top" arrow>
            <Switch
              checked={isActive}
              onChange={event => onToggleStatus(event.target.checked)}
              size="medium"
              sx={{
                '& .MuiSwitch-switchBase': {
                  '&.Mui-checked': {
                    '& + .MuiSwitch-track': {
                      backgroundColor: 'secondary.main'
                    }
                  }
                }
              }}
            />
          </Tooltip>
        </Box>
      </CardActions>
    </Card>
  );
};

// קומפוננטים חדשים לספר השלוחים

// כפתור אייקון צבעוני עם מסגרת
interface StaffMembersIconButtonProps {
  icon: React.ReactNode;
  tooltip: string;
  onClick: () => void;
  variant: 'phone' | 'whatsapp' | 'email';
  disabled?: boolean;
}

export const StaffMembersIconButton: React.FC<StaffMembersIconButtonProps> = ({
  icon,
  tooltip,
  onClick,
  variant,
  disabled = false
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'phone':
        return {
          borderColor: canvaColor,
          color: canvaColor,
          '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.12)' }
        };
      case 'whatsapp':
        return {
          borderColor: 'success.main',
          color: 'success.main',
          '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.12)' }
        };
      case 'email':
        return {
          borderColor: 'error.main',
          color: 'error.main',
          '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.12)' }
        };
    }
  };

  return (
    <Tooltip title={tooltip}>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        sx={{
          ...formStyles.StaffMembersBook.coloredIconButton,
          ...getVariantStyles()
        }}
      >
        <Box sx={formStyles.StaffMembersBook.iconBox}>{icon}</Box>
      </IconButton>
    </Tooltip>
  );
};

// כפתור חזרה מעוצב
interface StaffMembersBackButtonProps {
  onClick: () => void;
  children: React.ReactNode;
}

export const StaffMembersBackButton: React.FC<StaffMembersBackButtonProps> = ({ onClick, children }) => {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
      <Button onClick={onClick} startIcon={<UndoArrow />} sx={formStyles.StaffMembersBook.backButton}>
        {children}
      </Button>
    </Box>
  );
};

// קונטיינר חיפוש
interface SearchContainerProps {
  children: React.ReactNode;
}

export const SearchContainer: React.FC<SearchContainerProps> = ({ children }) => {
  return <Box sx={formStyles.StaffMembersBook.searchContainer}>{children}</Box>;
};

// קונטיינר תוצאות
interface ResultsContainerProps {
  children: React.ReactNode;
}

export const ResultsContainer: React.FC<ResultsContainerProps> = ({ children }) => {
  return <Box sx={formStyles.StaffMembersBook.resultsContainer}>{children}</Box>;
};

// פרטי שליח קומפקטיים
interface CompactStaffMembersDetailsProps {
  children: React.ReactNode;
}

export const CompactStaffMembersDetails: React.FC<CompactStaffMembersDetailsProps> = ({ children }) => {
  return <Box sx={formStyles.StaffMembersBook.compactDetails}>{children}</Box>;
};

// Stack לתוצאות עם גלילה
interface ResultsStackProps {
  children: React.ReactNode;
}

export const ResultsStack: React.FC<ResultsStackProps> = ({ children }) => {
  return (
    <Stack direction="column" spacing={2} sx={formStyles.StaffMembersBook.resultsStack}>
      {children}
    </Stack>
  );
};

// כרטיס מפה מעוצב
interface MapCardProps {
  children: React.ReactNode;
}

export const MapCard: React.FC<MapCardProps> = ({ children }) => {
  return <Box sx={formStyles.StaffMembersBook.mapCard}>{children}</Box>;
};

// תוכן התוצאות עם גלילה
interface ResultsContentProps {
  children: React.ReactNode;
}

export const ResultsContent: React.FC<ResultsContentProps> = ({ children }) => {
  return (
    <Box
      sx={{
        width: '100%',
        flexGrow: 1,
        overflowY: 'auto',
        paddingRight: 1,
        maxHeight: 'calc(100% - 60px)', // מקום לכפתור החזרה
        '&::-webkit-scrollbar': {
          width: 6
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0,0,0,0.1)',
          borderRadius: 3
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'primary.main',
          borderRadius: 3
        }
      }}
    >
      {children}
    </Box>
  );
};

// קומפוננט משותף לכרטיס אירועים ראשי
interface EventsMainCardProps {
  children: React.ReactNode;
}

export const EventsMainCard: React.FC<EventsMainCardProps> = ({ children }) => {
  return <Card sx={formStyles.events.mainCard}>{children}</Card>;
};

// קומפוננט משותף לכותרת סקציה עם רקע
interface EventsSectionTitleProps {
  title: string;
}

export const EventsSectionTitle: React.FC<EventsSectionTitleProps> = ({ title }) => {
  return (
    <Box sx={formStyles.events.sectionTitle}>
      <Typography
        variant="h6"
        sx={{
          color: 'text.primary',
          fontWeight: 600,
          textAlign: 'center'
        }}
      >
        {title}
      </Typography>
    </Box>
  );
};

// קומפוננט משותף לגלילה אופקית
interface EventsScrollContainerProps {
  children: React.ReactNode;
  justifyContent?: 'center' | 'flex-start';
}

export const EventsScrollContainer: React.FC<EventsScrollContainerProps> = ({
  children,
  justifyContent = 'flex-start'
}) => {
  return (
    <Box sx={formStyles.events.scrollContainer}>
      <Box
        sx={{
          ...formStyles.events.scrollBox,
          justifyContent: justifyContent
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

// קומפוננט משותף לכרטיסיית בחירת אירוע
interface EventSelectionCardProps {
  operationId: number;
  operationName: string;
  iconUrl?: string;
  isSelected: boolean;
  onClick: (operationId: number) => void;
  onDirectAdd: (operationId: number) => void;
}

export const EventSelectionCard: React.FC<EventSelectionCardProps> = ({
  operationId,
  operationName,
  iconUrl,
  isSelected,
  onClick,
  onDirectAdd
}) => {
  return (
    <Card
      sx={{
        ...formStyles.events.eventCard,
        ...(isSelected ? formStyles.events.selectedEventCard : {})
      }}
      onClick={() => onClick(operationId)}
    >
      <CardContent
        sx={{
          ...formStyles.events.eventCardContent,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          height: '100%'
        }}
      >
        {/* שם האירוע - למעלה */}
        <Typography
          sx={{
            ...formStyles.events.eventCardText,
            ...(isSelected ? formStyles.events.selectedEventCardText : {}),
            order: 1
          }}
        >
          {operationName}
        </Typography>

        {/* מעגל האייקון - באמצע */}
        <Box
          sx={{
            width: '50px',
            height: '50px',
            borderRadius: '50%', // עיגול מושלם
            backgroundColor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: isSelected ? '2px solid' : '2px solid transparent',
            borderColor: isSelected ? 'primary.main' : 'transparent',
            order: 2
          }}
        >
          {iconUrl ? (
            <img src={`/icons${iconUrl}`} alt={operationName} style={{ maxWidth: '24px', maxHeight: '24px' }} />
          ) : (
            <Box
              sx={{
                width: '24px',
                height: '24px',
                backgroundColor: 'grey.300',
                borderRadius: '50%'
              }}
            />
          )}
        </Box>

        {/* כפתור הוסף - למטה */}
        <Button
          sx={{
            order: 3,
            width: '90%', // ✅ שינוי מ-minWidth: '60px' לרוחב כמעט מלא
            height: '28px',
            borderRadius: '14px',
            backgroundColor: 'primary.main',
            color: 'white',
            fontSize: '20px',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'primary.dark',
              transform: 'scale(1.05)'
            }
          }}
          onClick={e => {
            e.stopPropagation();
            onDirectAdd(operationId);
          }}
        >
          +
        </Button>
      </CardContent>
    </Card>
  );
};

// קומפוננט משותף לכפתור הוספת אירוע
interface EventsAddButtonProps {
  onClick: () => void;
  disabled: boolean;
  children: React.ReactNode;
  icon?: React.ReactNode;
}

export const EventsAddButton: React.FC<EventsAddButtonProps> = ({ onClick, disabled, children, icon }) => {
  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      size="large"
      variant="contained"
      color="primary"
      endIcon={icon}
      sx={formStyles.events.addButton}
    >
      {children}
    </Button>
  );
};

// ===== קומפוננטים משותפים חדשים =====

// קומפוננט משותף להעלאת תמונה
interface ImageUploadProps {
  currentImage?: File | string;
  readOnly?: boolean;
  onImageChange: (image: File | string | undefined) => void;
  onSaveSuccess?: () => void;
  uploadUrl: string;
  deleteUrl: string;
  imageFieldName: string; // 'branchFiles' או 'assetFiles'
  imageType?: string; // 'chabadHouseImages' או 'image'
  imageLabel: string; // 'תמונת בית חב"ד' או 'תמונת בית כנסת'
  additionalFormData?: Record<string, string>; // נתונים נוספים לשליחה
  fetchUpdateData: (params: any) => Promise<any>; // פונקציית fetch מהקומפוננט האב
  loading?: boolean; // סטטוס loading מהקומפוננט האב
  isPublic?: boolean; // מצב ציבוריות לעיצוב אפור
  showSuccessMessage?: boolean; // האם להציג הודעות הצלחה נפרדות
}

export const SharedImageUpload: React.FC<ImageUploadProps> = ({
  currentImage,
  readOnly = false,
  onImageChange,
  onSaveSuccess,
  uploadUrl,
  deleteUrl,
  imageFieldName,
  imageType = 'image',
  imageLabel,
  additionalFormData = {},
  fetchUpdateData,
  loading = false,
  isPublic = true,
  showSuccessMessage = true
}) => {
  const { setSnackbar } = useAppContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // פונקציה לקבלת URL התמונה
  const getImageSrc = () => {
    if (!currentImage) return null;
    if (typeof currentImage === 'string') return currentImage;
    return URL.createObjectURL(currentImage);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setSnackbar({
        message: 'יש להעלות קובץ תמונה בלבד',
        open: true,
        severity: 'error'
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({
        message: 'קובץ התמונה גדול מדי',
        open: true,
        severity: 'error'
      });
      return;
    }

    setIsUploading(true);
    // עדכון מיידי של התמונה בUI - כמו בשליח
    onImageChange(file);

    try {
      // שמירה מיידית
      const formData = new FormData();
      formData.append(imageFieldName, file, imageType);

      // הוספת נתונים נוספים
      Object.entries(additionalFormData).forEach(([key, value]) => {
        formData.append(key, value);
      });

      await fetchUpdateData({
        method: 'POST',
        url: uploadUrl,
        data: formData
      });

      if (showSuccessMessage) {
        setSnackbar({
          open: true,
          message: 'התמונה הועלתה ונשמרה בהצלחה',
          severity: 'success'
        });
      }
      onSaveSuccess?.();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'שגיאה בהעלאת התמונה',
        severity: 'error'
      });
      // אם נכשל, חוזרים למצב המקורי
      onImageChange(undefined);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    setDeleteDialog(false);

    try {
      let data;

      if (imageFieldName === 'branchFiles') {
        // עבור Branch - שלח FormData עם השדה הנכון
        const formData = new FormData();
        formData.append('chabadHouseImages', ''); // שדה ריק למחיקה
        Object.entries(additionalFormData).forEach(([key, value]) => {
          formData.append(key, value);
        });
        data = formData;
      } else if (imageFieldName === 'familyImage') {
        // עבור StaffMember - שלח object עם השדה הנכון
        data = { familyImage: null };
      } else {
        // עבור Assets - שלח FormData
        const formData = new FormData();
        Object.entries(additionalFormData).forEach(([key, value]) => {
          formData.append(key, value);
        });
        formData.append('image', '');
        data = formData;
      }

      await fetchUpdateData({
        method: 'POST',
        url: deleteUrl,
        data: data
      });

      if (showSuccessMessage) {
        setSnackbar({
          open: true,
          message: 'התמונה נמחקה בהצלחה',
          severity: 'success'
        });
      }
      // רק onSaveSuccess - הuseEffect יעדכן את התמונה מהרענון
      onSaveSuccess?.();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'שגיאה במחיקת התמונה',
        severity: 'error'
      });
      // אם נכשל במחיקה, לא נשנה כלום
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box sx={{ pt: 1 }}>
      <Box sx={formStyles.centerContainer}>
        <input
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          ref={fileInputRef}
          onChange={handleFileChange}
          disabled={readOnly}
        />

        {getImageSrc() ? (
          <>
            {/* Loading overlay */}
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              {/* תצוגה מקדימה */}
              <Button
                variant="text"
                onClick={() => setPreviewOpen(true)}
                sx={{
                  p: 0,
                  borderRadius: 2,
                  overflow: 'hidden',
                  width: '120px',
                  height: '120px'
                }}
              >
                <img
                  src={getImageSrc() || ''}
                  alt={imageLabel}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    // הוספת אפקט אפור אם הכרטיס לא ציבורי
                    ...(isPublic ? {} : formStyles.imagePrivate)
                  }}
                />
              </Button>

              {/* Loading overlay */}
              {(isDeleting || isUploading) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2,
                    color: 'white',
                    fontSize: '0.8rem'
                  }}
                >
                  {isUploading ? 'מעלה...' : 'מוחק...'}
                </Box>
              )}
            </Box>

            {/* טקסט מתחת לתצוגה המקדימה */}
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
                fontWeight: 400,
                textAlign: 'center',
                mt: 0.5,
                mb: 1
              }}
            >
              {imageLabel}
            </Typography>

            {/* כפתור מחיקה בלבד */}
            {!readOnly && (
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Button
                  onClick={() => setDeleteDialog(true)}
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  disabled={isDeleting || isUploading}
                  sx={{
                    fontSize: '0.7rem',
                    height: '28px',
                    borderRadius: 2
                  }}
                >
                  מחק
                </Button>
              </Box>
            )}
          </>
        ) : (
          <>
            {/* כפתור העלאה */}
            <StyledIconButton
              onClick={() => !readOnly && !isUploading && fileInputRef.current?.click()}
              disabled={readOnly || isUploading}
              icon={<AddAPhotoIcon />}
              tooltip={isUploading ? 'מעלה...' : `העלה ${imageLabel}`}
              isPublic={isPublic}
              variant="warning"
              sx={{
                width: 60,
                height: 60,
                opacity: isUploading ? 0.5 : 1
              }}
            />

            {/* טקסט מתחת לכפתור */}
            <Typography variant="caption" sx={formStyles.forms.imageCaption}>
              {isUploading ? 'מעלה...' : imageLabel}
            </Typography>
          </>
        )}
      </Box>

      {/* Dialog אישור מחיקה */}
      <DynamicDialog
        open={deleteDialog}
        title="אישור מחיקה"
        subtitle={`האם אתה בטוח שברצונך למחוק את ${imageLabel}?`}
        handleOkClose={handleDeleteConfirm}
        handleNotOkClose={() => setDeleteDialog(false)}
      />

      {/* Dialog תצוגה מקדימה */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {imageLabel}
          <IconButton
            onClick={() => setPreviewOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {getImageSrc() && (
            <img
              src={getImageSrc() || ''}
              alt={imageLabel}
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// קומפוננט משותף לסוויץ פאבליק
interface PublicSwitchProps {
  isPublic: boolean;
  readOnly?: boolean;
  onToggle: (checked: boolean) => void;
  label?: string;
}

export const SharedPublicSwitch: React.FC<PublicSwitchProps> = ({
  isPublic,
  readOnly = false,
  onToggle,
  label = 'מוצג באתר'
}) => {
  if (readOnly) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 16,
        right: 16,
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row-reverse',
        gap: 1,
        zIndex: 2,
        transform: 'scaleX(-1)'
      }}
    >
      <Typography variant="caption" sx={{ fontSize: '12px', color: 'text.secondary', transform: 'scaleX(-1)' }}>
        {label}
      </Typography>
      <Switch
        checked={isPublic}
        onChange={e => onToggle(e.target.checked)}
        size="medium"
        sx={{
          '& .MuiSwitch-switchBase': {
            '&.Mui-checked': {
              '& + .MuiSwitch-track': {
                backgroundColor: 'warning.main'
              }
            }
          }
        }}
      />
    </Box>
  );
};

// Hook משותף לסוויץ פאבליק (עבור Assets)
interface UsePublicSwitchProps {
  initialValue: boolean;
  updateUrl: string;
  assetType: string;
  onSuccess?: () => void;
  fetchUpdateData: (params: any) => Promise<any>; // פונקציית fetch מהקומפוננט האב
}

export const usePublicSwitch = ({
  initialValue,
  updateUrl,
  assetType,
  onSuccess,
  fetchUpdateData
}: UsePublicSwitchProps) => {
  const { setSnackbar } = useAppContext();
  const [isPublic, setIsPublic] = useState(initialValue);

  const handleToggle = (checked: boolean) => {
    setIsPublic(checked);

    const formData = new FormData();
    formData.append('type', assetType);
    formData.append('isPublic', checked.toString());

    fetchUpdateData({
      method: 'POST',
      url: updateUrl,
      data: formData
    })
      .then(() => {
        setSnackbar({
          open: true,
          message: `${assetType === 'בית כנסת' ? 'הבית כנסת' : 'החנות'} ${checked ? 'פורסם' : 'לא פורסם'} באתר`,
          severity: 'success'
        });
        onSuccess?.();
      })
      .catch(() => {
        setSnackbar({
          open: true,
          message: 'שגיאה בעדכון הסטטוס',
          severity: 'error'
        });
        setIsPublic(!checked);
      });
  };

  return { isPublic, handleToggle, setIsPublic };
};

// Hook מותאם לסוויץ פאבליק עבור בראנץ
interface UseBranchPublicSwitchProps {
  initialValue: boolean;
  updateUrl: string;
  onSuccess?: () => void;
  fetchUpdateData: (params: any) => Promise<any>; // פונקציית fetch מהקומפוננט האב
}

export const useBranchPublicSwitch = ({
  initialValue,
  updateUrl,
  onSuccess,
  fetchUpdateData
}: UseBranchPublicSwitchProps) => {
  const { setSnackbar } = useAppContext();
  const [isPublic, setIsPublic] = useState(initialValue);

  const handleToggle = async (checked: boolean) => {
    setIsPublic(checked);

    try {
      const formData = new FormData();
      formData.append('isPublic', checked.toString());

      await fetchUpdateData({
        method: 'POST',
        url: updateUrl,
        data: formData
      });

      // הודעת הצלחה מוסרה - תוצג הודעה כללית בסוף השמירה

      onSuccess?.();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'שגיאה בעדכון מצב הפרסום',
        severity: 'error'
      });
      // אם נכשל, חוזרים למצב הקודם
      setIsPublic(!checked);
    }
  };

  return { isPublic, handleToggle, setIsPublic };
};

// Hook מותאם לסוויץ פאבליק עבור שליח
interface UseStaffMemberPublicSwitchProps {
  initialValue: boolean;
  updateUrl: string;
  onSuccess?: () => void;
  fetchUpdateData: (params: any) => Promise<any>; // פונקציית fetch מהקומפוננט האב
}

export const useStaffMemberPublicSwitch = ({
  initialValue,
  updateUrl,
  onSuccess,
  fetchUpdateData
}: UseStaffMemberPublicSwitchProps) => {
  const { setSnackbar } = useAppContext();
  const [isPublic, setIsPublic] = useState(initialValue);

  const handleToggle = async (checked: boolean) => {
    setIsPublic(checked);

    try {
      // עבור שליח נשלח object רגיל (לא FormData)
      const data = { isPublic: checked };

      await fetchUpdateData({
        method: 'POST',
        url: updateUrl,
        data: data
      });

      // הודעת הצלחה מוסרה - תוצג הודעה כללית בסוף השמירה

      onSuccess?.();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'שגיאה בעדכון מצב הפרסום',
        severity: 'error'
      });
      // אם נכשל, חוזרים למצב הקודם
      setIsPublic(!checked);
    }
  };

  return { isPublic, handleToggle, setIsPublic };
};

// Hook משותף לGoogle Maps Autocomplete
export const useGoogleMapsAutocomplete = (initialAddress?: string) => {
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [placeholder, setPlaceholder] = useState(initialAddress || 'כתובת מלאה');
  const [clearAddress, setClearAddress] = useState(false);

  const onLoad = (autocompleteLoad: google.maps.places.Autocomplete) => {
    setAutocomplete(autocompleteLoad);
  };

  const handleClearAddress = () => {
    setClearAddress(true);
    setPlaceholder('');
  };

  const getAddressData = () => {
    const place = autocomplete?.getPlace();
    let position = undefined;
    let fullAddress = '';

    if (clearAddress) {
      return { fullAddress: '', position: { lat: 0, lng: 0 }, clearPosition: true };
    }

    if (place?.geometry?.location) {
      try {
        position = {
          lat: place.geometry.location.lat() || 0,
          lng: place.geometry.location.lng() || 0
        };
      } catch (error) {
        console.error('Error getting location:', error);
      }
    }

    if (place) {
      if (place.name === place.vicinity) {
        fullAddress = place.name || '';
      } else {
        fullAddress = [place.name, place.vicinity].filter(Boolean).join(', ');
      }
    }

    return { fullAddress, position, clearPosition: clearAddress };
  };

  const resetAddress = (newAddress?: string) => {
    setPlaceholder(newAddress || 'כתובת מלאה');
    setClearAddress(false);
  };

  return {
    onLoad,
    placeholder,
    handleClearAddress,
    getAddressData,
    resetAddress,
    clearAddress
  };
};

// קומפוננט משותף לשדה כתובת עם Google Maps
interface AddressFieldProps {
  label: string;
  placeholder: string;
  readOnly?: boolean;
  onLoad: (autocomplete: google.maps.places.Autocomplete) => void;
  onClearAddress: () => void;
  showClearButton?: boolean;
}

export const SharedAddressField: React.FC<AddressFieldProps> = ({
  label,
  placeholder,
  readOnly = false,
  onLoad,
  onClearAddress,
  showClearButton = true
}) => {
  return (
    <>
      <FieldLabel label={label} />
      <Box sx={{ ...formStyles.inputField, position: 'relative' }}>
        <GoogleMapAutocomplete onLoad={onLoad} placeholder={placeholder} label="" disabled={readOnly} />
        {showClearButton && placeholder && placeholder !== 'כתובת מלאה' && !readOnly && (
          <IconButton
            size="small"
            color="primary"
            onClick={onClearAddress}
            sx={{
              position: 'absolute',
              top: '50%',
              right: '10px',
              transform: 'translateY(-50%)',
              zIndex: 1
            }}
            aria-label="נקה כתובת"
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </>
  );
};

// קומפוננט משותף לשדה אתר אינטרנט עם כפתור
interface WebsiteFieldProps {
  label: string;
  name: string;
  readOnly?: boolean;
  websiteValue: string;
  isPublic?: boolean;
}

export const SharedWebsiteField: React.FC<WebsiteFieldProps> = ({
  label,
  name,
  readOnly = false,
  websiteValue,
  isPublic = true
}) => {
  const handleOpenWebsite = () => {
    if (websiteValue) {
      const url = websiteValue.startsWith('http') ? websiteValue : `https://${websiteValue}`;
      window.open(url, '_blank');
    }
  };

  return (
    <>
      <FieldLabel label={label} />
      <Box sx={formStyles.forms.fieldWithIcon}>
        <StyledIconButton
          onClick={handleOpenWebsite}
          disabled={readOnly}
          icon={<LanguageIcon />}
          tooltip="פתח אתר אינטרנט"
          variant="warning"
          sx={formStyles.forms.websiteButton}
          isPublic={isPublic}
        />
        <Box sx={formStyles.forms.flexBox}>
          <FormField label="" name={name} readOnly={readOnly} />
        </Box>
      </Box>
    </>
  );
};

// קומפוננט משותף לשדה פייסבוק עם כפתור
interface FacebookFieldProps {
  label: string;
  name: string;
  readOnly?: boolean;
  facebookValue: string;
  isPublic?: boolean;
}

export const SharedFacebookField: React.FC<FacebookFieldProps> = ({
  label,
  name,
  readOnly = false,
  facebookValue,
  isPublic = true
}) => {
  const handleOpenFacebook = () => {
    if (facebookValue) {
      const url = facebookValue.startsWith('http') ? facebookValue : `https://facebook.com/${facebookValue}`;
      window.open(url, '_blank');
    }
  };

  return (
    <>
      <FieldLabel label={label} />
      <Box sx={formStyles.forms.fieldWithIcon}>
        <FacebookButton
          onClick={handleOpenFacebook}
          disabled={readOnly}
          title="פתח דף פייסבוק"
          icon={<FacebookIcon />}
          isPublic={isPublic}
        />
        <Box sx={formStyles.forms.flexBox}>
          <FormField label="" name={name} readOnly={readOnly} />
        </Box>
      </Box>
    </>
  );
};
